import { z } from 'zod';

const key = z
  .string()
  .regex(/^[a-z][a-z0-9-]*$/)
  .max(64)
  .refine(
    (value) => !['constructor', 'prototype', '__proto__'].includes(value),
    'Reserved capability name',
  );
const origin = z
  .string()
  .max(300)
  .refine((value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && url.origin === value && !url.username && !url.password;
    } catch {
      return false;
    }
  }, 'Expected a canonical HTTPS origin');

/** Declarations are requests. The platform separately grants and bounds each capability. */
export const skillCapabilitiesSchema = z
  .object({
    publicProbes: z.array(key).max(4).optional(),
    /** Explicitly shared data within one workflow invocation, never persistent/public cache. */
    sharedLookups: z.array(key).max(4).optional(),
    timingAdvice: z.boolean().optional(),
    geographicSignals: z.boolean().optional(),
    hostedBrowser: z
      .object({
        /** Explicit navigation only; source page scripts and redirects are not network-isolated. */
        navigationOrigins: z
          .array(origin)
          .min(1)
          .max(16)
          .refine((values) => new Set(values).size === values.length, 'Duplicate browser origin'),
        timeoutSeconds: z.number().int().min(1).max(600),
        maxSessions: z.number().int().min(1).max(4),
      })
      .strict()
      .optional(),
    credentials: z
      .array(
        z
          .object({
            name: key,
            fields: z
              .array(key)
              .min(1)
              .max(16)
              .refine(
                (values) => new Set(values).size === values.length,
                'Duplicate credential field',
              ),
          })
          .strict(),
      )
      .max(16)
      .refine(
        (values) => new Set(values.map((value) => value.name)).size === values.length,
        'Duplicate credential name',
      )
      .optional(),
  })
  .strict();

/** Provider identity and credentials remain inside the platform transport. */
export type HostedBrowserSession = {
  readonly expiresAt: number;
  navigate: (url: string, deadline?: number) => Promise<void>;
  evaluate: <T>(expression: string, deadline?: number) => Promise<T>;
  /** Bounded direct HTTP using private browser cookies; no cookie access is exposed. */
  download?: (
    url: string,
    deadline?: number,
    maxBytes?: number,
  ) => Promise<{ contentType: string; bytes: Uint8Array; finalUrl?: string }>;
  close: () => Promise<void>;
};
export type SkillCapabilities = {
  hostedBrowser?: { open: (options?: { signal?: AbortSignal }) => Promise<HostedBrowserSession> };
  /** The platform returns only this skill's explicitly declared and granted fields. */
  credential?: (name: string) => Readonly<Record<string, string>> | undefined;
};
